/**
 * slitScanChrono — chrono-displacement slit-scan cinematography.
 *
 * Slit-scan cinematography: a moving slit captures one column per
 * frame, and the resulting image unrolls the time axis. The
 * leftmost column is the oldest pixel, the rightmost is the newest.
 *
 * One mechanism: a synthetic scene with a figure walking across
 * the screen, sampled at 80 different time offsets across the X
 * axis. The temporal smearing makes the figure's walk read as a
 * horizontal stream. The depth-color tells you which time you're
 * looking at — cool/warm gradient.
 *
 * No panels, no timecode, no "now" line, no CRT scanlines. Just
 * the unrolled time axis.
 *
 * Reference: Chris Marker "La Jetée", thermal cameras, Matrix
 * lobby shots.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SlitScanChronoPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// The slit-scanned scene: a figure walks left-to-right across a
// dark street. The scene is minimal — just the figure, the
// sidewalk, and a single streetlight.
vec3 sceneAt(vec2 uv, float t) {
  // Sky gradient (top half).
  vec3 sky = mix(vec3(0.02, 0.025, 0.05), vec3(0.04, 0.06, 0.12), 1.0 - uv.y);
  // Sidewalk (bottom half).
  vec3 ground = vec3(0.04, 0.05, 0.07);
  vec3 col = mix(ground, sky, smoothstep(0.45, 0.55, uv.y));

  // Streetlight pool.
  vec2 pool = vec2(0.5, 0.3);
  float pd = length((uv - pool) * vec2(1.0, 2.5));
  col += vec3(1.0, 0.6, 0.25) * exp(-pd * pd * 8.0) * 0.6;

  // Figure walking across the scene.
  float walkPhase = fract(t * 0.10);
  float figX = mix(-0.10, 1.10, walkPhase);
  float bobY = 0.04 * abs(sin(t * 6.0));
  float figY = 0.30 + bobY;
  vec2 figPos = vec2(figX, figY);
  // Body: capsule.
  float dToFig = length((uv - figPos) * vec2(1.0, 2.0));
  float body = exp(-dToFig * dToFig * 80.0);
  col = mix(col, vec3(0.02, 0.03, 0.05), body * 0.95);
  // Rim light from the streetlight.
  float rim = (walkPhase < 0.5) ? uv.x : (1.0 - uv.x);
  col += vec3(1.0, 0.55, 0.25) * body * smoothstep(0.0, 0.5, rim) * 0.5;

  return col;
}

void main() {
  vec2 uv = vUV;
  float T = uTime;

  // 80 columns of history.
  // Column 0 = 3.2 seconds ago, column 79 = present.
  float corridorBottom = 0.0;
  float corridorTop = 1.0;
  if (uv.y < corridorBottom || uv.y > corridorTop) {
    outColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }

  const int HISTORY = 80;
  float colWidth = 1.0 / float(HISTORY);
  int frameIdx = int(uv.x * float(HISTORY));
  if (frameIdx >= HISTORY) frameIdx = HISTORY - 1;
  float tOffset = float(frameIdx) * 0.04;
  float sceneT = T - tOffset;

  // Sub-pixel sample within the column.
  vec2 sampleUV = vec2(
    fract(uv.x * float(HISTORY)) * 1.0,
    uv.y
  );
  vec3 sceneCol = sceneAt(sampleUV, sceneT);

  // Depth color: oldest = cool, newest = warm.
  float depth = 1.0 - float(frameIdx) / float(HISTORY);
  vec3 graded = mix(
    sceneCol * vec3(0.7, 1.0, 1.1),    // oldest: shadow teal
    sceneCol * vec3(1.1, 0.95, 0.7),   // newest: highlight orange
    depth
  );

  // Subtle vignette — keeps the focus on the corridor.
  vec2 vc = uv - vec2(0.5, 0.5);
  vc.x *= 1.0 / (uResolution.x / uResolution.y);
  float vig = 1.0 - dot(vc, vc) * 0.2;
  graded *= clamp(vig, 0.0, 1.0);

  // Tone mapping + gamma.
  vec3 col = (graded * (2.51 * graded + 0.03)) / (graded * (2.43 * graded + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeSlitScanChronoBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeSlitScanChronoRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createSlitScanChronoPipeline(gl: WebGL2RenderingContext): SlitScanChronoPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawSlitScanChrono(gl: WebGL2RenderingContext, p: SlitScanChronoPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
